import { Controller } from "@hotwired/stimulus";

// Step2's late-availability check.
//
// F-ENGINE kicks off a live GDS re-check of the chosen itinerary the moment
// it is selected (SearchController#do_check -> Sidekiq::LateCheckJob ->
// Engine#check) and caches the verdict under check_uuid_<search>_<uuid>.
// /check/:id (SearchController#check_id, which relays FAPI::Search.check_id)
// reports that verdict: true, false, or "still running". Nothing else in the
// funnel ever asks for it.
//
// Without this the customer fills in the whole booking form for an itinerary
// the airline has already refused, submits it, and F-ENGINE dutifully builds
// the order and its orderlines -- then Engine#confirm fails, the order is
// cancelled with "Confirm failure", and no PNR is ever created. On qa and
// production that failure happens in Sidekiq (FinalizeOrderlinesJob is
// enqueued async when the gate has addons), so the make_reservation response
// is still status "ok" and the customer is sent on to step3 to pay for a
// booking that does not exist. The old design polled this on every step2 load
// (components/common/check_status.ts, imported by packs/desktop/step2.js) and
// showed the dialog below instead; that file went with the jQuery/fancybox
// removal, and the check went with it.
//
// Timings are the old file's: the engine's own check joins on per-module GDS
// threads for up to 45s, plus Sidekiq queue time, so /check/:id answers
// immediately with whatever it knows and this waits the rest out here rather
// than holding a connection open across three independent timeouts.
const POLL_INTERVAL_MS = 1000;
const MAX_ATTEMPTS = 60; // ~60s, comfortably past the ~45s worst case

export default class extends Controller {
  static targets = ["dialog"];
  static values = { url: String };

  connect() {
    if (!this.hasUrlValue || !this.urlValue) return;
    this.stopped = false;
    this.poll(0);
  }

  disconnect() {
    this.stopped = true;
    clearTimeout(this.timeout);
  }

  async poll(attempt) {
    if (this.stopped) return;

    let data = null;
    try {
      const response = await fetch(this.urlValue, {
        headers: { Accept: "application/json" },
        credentials: "same-origin"
      });
      data = await response.json();
    } catch (e) {
      // A dropped request is not an answer. The jQuery original had no error
      // callback at all, so one blip ended the polling for good; counting it
      // as an attempt instead keeps the same upper bound without that.
      data = null;
    }
    if (this.stopped) return;

    if (data && data.status === false) {
      this.showUnavailable();
      return;
    }

    if (data && data.status === true) {
      this.applyExtras(data);
      return;
    }

    if (attempt < MAX_ATTEMPTS - 1) {
      this.timeout = setTimeout(() => this.poll(attempt + 1), POLL_INTERVAL_MS);
      return;
    }

    // Never got a definitive answer in time -- treat the itinerary as
    // available, exactly as before. The engine's old 30s server-side cap
    // gave up the same way and this same path already treated that as
    // success; blocking the booking on a check that never reported back
    // would lose real bookings.
    this.applyExtras(data);
  }

  // Same contract as the spinner screen's own dialog
  // (step2/spinner.html.erb): however it is closed, the customer goes back
  // to a fresh search -- there is no dismiss that leaves them on a step2 for
  // seats that are gone.
  showUnavailable() {
    if (!this.hasDialogTarget) return;
    this.dialogTarget.classList.remove("hidden");
  }

  close() {
    window.location.href = "/";
  }

  closeOnBackdrop(event) {
    if (event.target === event.currentTarget) this.close();
  }

  // #ltd_div/#ltd_value live in _itinerary_step2.html.erb, outside this
  // controller's element, so they are looked up by id the way the old file
  // did rather than being targets. The row renders hidden and only appears
  // once there is a date to put in it.
  applyExtras(data) {
    const value = data && data.extras && data.extras.last_ticketing_date;
    if (!value) return;

    const container = document.getElementById("ltd_div");
    const slot = document.getElementById("ltd_value");
    if (!container || !slot) return;

    slot.textContent = value;
    container.classList.remove("hidden");
  }
}
